/* eslint-disable react/react-in-jsx-scope */
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import { useFormik } from 'formik';
import PageWrapper from '../../../layout/PageWrapper/PageWrapper';
import Page from '../../../layout/Page/Page';
import Card, { CardBody } from '../../../components/bootstrap/Card';
import FormGroup from '../../../components/bootstrap/forms/FormGroup';
import Input from '../../../components/bootstrap/forms/Input';
import Button from '../../../components/bootstrap/Button';
import Logo from '../../../components/Logo';
import useDarkMode from '../../../hooks/useDarkMode';
import Spinner from '../../../components/bootstrap/Spinner';
import Alert from '../../../components/bootstrap/Alert';
import showNotification from '../../../components/extras/showNotification';
import Icon from '../../../components/icon/Icon';
import { selectLoginResponse } from '../../../redux/login/login.selector';
import {
	loginStart,
	//loginResponseRestStart,
	addPersistDataStart,
} from '../../../redux/login/login.action';
import { loginPages } from '../../../menu';

const Login = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { darkModeStatus } = useDarkMode();

	const loginResponse = useSelector(selectLoginResponse);
	const [isShowError, setShowErrorCode] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	// Handle successful login
	useEffect(() => {
		if (loginResponse) {
			console.log('Login response in component:', loginResponse);
			if (loginResponse.status === 'success') {
				setIsLoading(false);
				dispatch(addPersistDataStart(loginResponse.data));
				navigate(loginPages.dashboard.path);
			} else {
				setShowErrorCode(true);
				setIsLoading(false);
				showNotification(
					<span className='d-flex align-items-center'>
						<Icon icon='Info' size='lg' className='me-1' />
						<span>Error</span>
					</span>,
					loginResponse.message || 'Please, Check your login credentials',
					'danger',
				);
			}
			//dispatch(loginResponseRestStart());
		}
	}, [loginResponse, navigate, dispatch]);

	const formik = useFormik({
		enableReinitialize: true,
		initialValues: {
			userPhone: '',
			userPassword: '',
		},
		validate: (values) => {
			const errors: { userPhone?: string; userPassword?: string } = {};

			if (!values.userPhone) {
				errors.userPhone = 'Required';
			}

			if (!values.userPassword) {
				errors.userPassword = 'Required';
			}

			return errors;
		},
		validateOnChange: false,
		onSubmit: (values) => {
			setIsLoading(true);
			setShowErrorCode(false);

			// Dispatch login action with correct parameter names
			dispatch(
				loginStart({
					userPhone: values.userPhone,
					userPassword: values.userPassword,
				}),
			);
		},
	});

	function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
		if (e.key === 'Enter') {
			formik.handleSubmit();
		}
	}

	return (
		<PageWrapper isProtected={false} title='Login' className='bg-success'>
			<Page>
				<div className='row h-100 align-items-center justify-content-center'>
					<div className='col-xl-4 col-lg-6 col-md-8 shadow-3d-container'>
						<Card className='shadow-3d-dark' data-tour='login-page'>
							<CardBody>
								<div className='text-center my-5'>
									<Link
										to='/'
										className={classNames(
											'text-decoration-none  fw-bold display-2',
											{
												'text-dark': !darkModeStatus,
												'text-light': darkModeStatus,
											},
										)}
										aria-label='Facit'>
										<Logo width={200} />
									</Link>
								</div>

								<div>
									<div className='text-center h2 fw-bold mt-5'>Welcome,</div>
									<div className='text-center h5 text-muted mb-5'>LOGIN</div>
								</div>

								{isShowError && (
									<div className='mb-4'>
										<Alert
											color='warning'
											isLight
											icon='Report'
											className='mb-0'>
											Please, Check your Phone Number and Password
										</Alert>
									</div>
								)}

								<form className='row g-4' onSubmit={formik.handleSubmit}>
									<div className='col-12'>
										<FormGroup id='userPhone' isFloating label='Phone Number'>
											<Input
												size='sm'
												type='tel'
												autoComplete='username'
												value={formik.values.userPhone}
												isTouched={formik.touched.userPhone}
												invalidFeedback={formik.errors.userPhone}
												isValid={formik.isValid}
												onChange={(
													e: React.ChangeEvent<HTMLInputElement>,
												) => {
													const value = e.target.value
														.replace(/\D/g, '')
														.slice(0, 10);
													formik.setFieldValue('userPhone', value);
												}}
												onBlur={formik.handleBlur}
												onFocus={() => {
													formik.setErrors({});
												}}
											/>
										</FormGroup>
									</div>
									<div className='col-12'>
										<FormGroup id='userPassword' isFloating label='Password'>
											<Input
												type='password'
												size='sm'
												autoComplete='current-password'
												value={formik.values.userPassword}
												isTouched={formik.touched.userPassword}
												invalidFeedback={formik.errors.userPassword}
												validFeedback='Looks good!'
												isValid={formik.isValid}
												onChange={formik.handleChange}
												onBlur={formik.handleBlur}
												onKeyDown={handleKeyDown}
											/>
										</FormGroup>
									</div>
									<div className='col-12'>
										<Button
											color='dark'
											size='sm'
											className='w-100 py-3'
											isDisable={isLoading}
											type='submit'>
											{isLoading && <Spinner isSmall inButton isGrow />}
											LOGIN
										</Button>
									</div>
								</form>
							</CardBody>
						</Card>
					</div>
				</div>
			</Page>
		</PageWrapper>
	);
};

export default Login;
